/**
 * Exclude Engine Tab — flat-list rule builder.
 *
 * Top toolbar holds the bulk-layer actions + clear + presets. Below it,
 * a list of top-level rule groups (one card per group). Each group has
 * its own AND/OR connector, "Applies to" layer chips, conditions, and
 * `+ Condition / + NOT` buttons. Adding a NEW group lives at the bottom
 * of the list — there is intentionally no `+ Group` affordance inside
 * a group (nesting tops out at depth 1).
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { ListChecks, Plus } from 'lucide-react';
import type {
  CapacityRule,
  ExcludeRulesTree,
  FormulaGroup,
} from '@trafficpulse/shared';


import ExcludeGroupCard from './exclude/ExcludeGroupCard';
import { weightIssueByGroupId } from './exclude/group/weight';
import { ExcludeToolbarStrip } from './exclude/toolbar';
import type { ExcludePreset } from './exclude/exclude-presets';
import ExcludeEmptyState from './exclude/ExcludeEmptyState';
import ExcludeHelpContent from './exclude/ExcludeHelpContent';
import TrackerCapacitySection from './exclude/capacity/TrackerCapacitySection';
import { HowItWorksPanel } from '../components/help';
import { presetToGroup } from '../utils/preset-to-group';
import {
  ALL_LAYERS,
  makeGroup,
  removeGroup,
  replaceGroup,
  setAllLayers,
  treeToView,
  viewToTree,
  type ExcludeView,
} from '../utils/exclude-view-model';

export interface ExcludeEngineTabProps {
  excludeRules: ExcludeRulesTree;
  onChange: (next: ExcludeRulesTree) => void;
  capacityRules?: CapacityRule[];
  onCapacityRulesChange?: (next: CapacityRule[]) => void;
  /** Live formula groups — drives each rule's "inherits X" default-range hint. */
  formulaGroups?: FormulaGroup[];
  /** Library set controls (Save as set… / Load set) — composed upstream. */
  libraryActions?: ReactNode;
}

const NOOP_CAPACITY = (): void => {};

const ExcludeEngineTab: FC<ExcludeEngineTabProps> = ({
  excludeRules,
  onChange,
  capacityRules = [],
  onCapacityRulesChange = NOOP_CAPACITY,
  formulaGroups = [],
  libraryActions,
}) => {
  // Keep an internal flat view-model. Sync FROM excludeRules only when the
  // tree changes through an external (non-self) update, so user authoring
  // is never disturbed by our own serialisation round-trips.
  const lastSerializedRef = useRef<ExcludeRulesTree | null>(null);
  const [view, setView] = useState<ExcludeView>(() => treeToView(excludeRules));

  useEffect(() => {
    if (lastSerializedRef.current === excludeRules) return;
    setView(treeToView(excludeRules));
  }, [excludeRules]);

  const commit = (next: ExcludeView) => {
    setView(next);
    const tree = viewToTree(next);
    lastSerializedRef.current = tree;
    onChange(tree);
  };

  const handleAddGroup = () => commit([...view, makeGroup(ALL_LAYERS)]);
  const handleSetAllLayers = (layers: ReadonlyArray<typeof ALL_LAYERS[number]>) =>
    commit(setAllLayers(view, layers));
  const handleClearAll = () => commit([]);
  const handlePreset = (preset: ExcludePreset) =>
    commit([...view, presetToGroup(preset)]);

  const groupCount = useMemo(() => view.length, [view]);
  const weightIssues = useMemo(() => weightIssueByGroupId(view), [view]);

  return (
    <div className="space-y-4">
      <TrackerCapacitySection rules={capacityRules} onChange={onCapacityRulesChange} />

      <section className="space-y-2">
        <div className="flex items-center gap-2">
          <ListChecks className="h-4 w-4 text-muted-foreground" aria-hidden />
          <h3 className="text-[13px] font-semibold">Rules</h3>
          <span className="text-[11px] text-muted-foreground">
            Checked on every update — matching elements get the rule&apos;s action
          </span>
        </div>

        <HowItWorksPanel title="Rules" storageKey="fopt:help:exclusions">
          <ExcludeHelpContent />
        </HowItWorksPanel>

        <ExcludeToolbarStrip
          groupCount={groupCount}
          onSetAllLayers={handleSetAllLayers}
          onClearAll={handleClearAll}
          onApplyPreset={handlePreset}
          libraryActions={libraryActions}
        />

        {view.length === 0 ? (
          <ExcludeEmptyState onAdd={handleAddGroup} />
        ) : (
          <div className="space-y-2.5">
            {view.map((g, i) => (
              <ExcludeGroupCard
                key={g.id}
                index={i + 1}
                group={g}
                onChange={(next) => commit(replaceGroup(view, next))}
                onRemove={() => commit(removeGroup(view, g.id))}
                formulaGroups={formulaGroups}
                weightIssue={weightIssues.get(g.id)}
              />
            ))}
            <button
              type="button"
              onClick={handleAddGroup}
              className="inline-flex h-8 w-full items-center justify-center gap-1 rounded-md border border-dashed border-border text-[12px] font-medium text-muted-foreground transition-colors hover:border-primary/50 hover:text-primary"
            >
              <Plus className="h-3.5 w-3.5" aria-hidden />
              Add rule
            </button>
          </div>
        )}
      </section>
    </div>
  );
};

export default ExcludeEngineTab;
